import type {
    PricePerformancePoint,
    PricePerformanceResponse,
    StarsRankChangePoint,
} from '../api/chartDataTypes';
import { formatLongDate } from './perf10kFormat';

export type PricePerformanceSummary = {
    /** Plain-text version — used by the NVDA live region. */
    text: string;
    /** Same content with key values wrapped in <strong> for the visual banner.
     *  Dynamic strings sourced from the backend (e.g. `index_label`) are
     *  HTML-escaped because the shell splices this via dangerouslySetInnerHTML. */
    html: string;
};

/** Minimal HTML escape — covers the characters a backend label could plausibly
 *  emit. Kept inline (mirrors creditQualityFormat) because the surface is tiny. */
function escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Returns the entry in `series` whose date is closest to but not after
 * `targetIsoDate`. Exact matches win. If every entry is strictly newer than
 * the target, returns undefined. Generic over `{ date: string }` so it works
 * for index points, target prices, and STARS rank changes uniformly.
 *
 * ISO 'YYYY-MM-DD' is lexicographically comparable — string compare matches
 * the rest of the chartReader code (cf. `getLatestShoutTnaPoint`).
 */
export function findNearestOlderOrEqual<T extends { date: string }>(
    series: readonly T[],
    targetIsoDate: string,
): T | undefined {
    let best: T | undefined;
    for (const entry of series) {
        if (entry.date > targetIsoDate) continue;
        if (!best || entry.date > best.date) best = entry;
    }
    return best;
}

const STARS_RANK_LABEL: Record<1 | 2 | 3 | 4 | 5, string> = {
    1: 'Strong Sell',
    2: 'Sell',
    3: 'Hold',
    4: 'Buy',
    5: 'Strong Buy',
};

function starsRankLabel(entry: StarsRankChangePoint): string | null {
    return STARS_RANK_LABEL[entry.rank] ?? null;
}

/** "up" for non-negative pct_change, "down" for negative. Zero reads as "up"
 *  for consistency with the previous +0.00% convention. */
export function describeDirection(pct: number): 'up' | 'down' {
    return pct < 0 ? 'down' : 'up';
}

/** Unsigned two-decimal percent: `28.50%`, `3.10%`, `0.00%`. Direction is
 *  conveyed separately via `describeDirection`. */
export function formatAbsolutePercent(pct: number): string {
    return `${Math.abs(pct).toFixed(2)}%`;
}

/** Currency-prefixed price: `USD 73.50`, `HKD 40.00`. Falls back to the bare
 *  number when `currency` is empty so the panel doesn't render a leading space. */
function formatCurrencyPrice(value: number, currency: string): string {
    const number = value.toFixed(2);
    return currency ? `${currency} ${number}` : number;
}

/**
 * Builds the summary for a chosen "as-of" point in `data.company_price` plus
 * the nearest at-or-before entries for STARS rank, target price, and index
 * pct_change. Returns null only when `data.company_price` is empty.
 *
 * Format:
 *   "As of <date>, the share price was <currency> <price>
 *    (up/down <abs%> since <first date>)[, while <index> was up/down <abs%>].
 *    STARS rating was <rank> — <label>. Target price was <currency> <value>."
 *
 * Missing pieces are silently omitted — never speaks "undefined" or "not
 * available". The "since X" anchor stays fixed at the first company_price
 * entry even when the user picks an earlier point.
 */
export function buildPricePerformanceSummary(
    response: PricePerformanceResponse,
    chosenPoint: PricePerformancePoint,
): PricePerformanceSummary | null {
    const { data, currency } = response;
    if (data.company_price.length === 0) return null;

    const companyFirstDate = data.company_price[0].date;

    const dateLong = formatLongDate(chosenPoint.date);
    const priceStr = formatCurrencyPrice(chosenPoint.price, currency);
    const companyDir = describeDirection(chosenPoint.pct_change);
    const companyPct = formatAbsolutePercent(chosenPoint.pct_change);
    const companySince = formatLongDate(companyFirstDate);

    let text = `As of ${dateLong}, the share price was ${priceStr} (${companyDir} ${companyPct} since ${companySince})`;
    let html =
        `As of ${dateLong}, the share price was <strong>${priceStr}</strong> ` +
        `(<strong>${companyDir} ${companyPct}</strong> since ${companySince})`;

    const indexEntry = findNearestOlderOrEqual(data.index.points, chosenPoint.date);
    const indexLabel = data.index_label?.trim() ?? '';
    if (indexLabel && indexEntry) {
        const indexDir = describeDirection(indexEntry.pct_change);
        const indexPct = formatAbsolutePercent(indexEntry.pct_change);
        text += `, while ${indexLabel} was ${indexDir} ${indexPct}`;
        html += `, while ${escapeHtml(indexLabel)} was <strong>${indexDir} ${indexPct}</strong>`;
    }

    text += '.';
    html += '.';

    const starsEntry = findNearestOlderOrEqual(data.stars_rank_changes, chosenPoint.date);
    if (starsEntry) {
        const label = starsRankLabel(starsEntry);
        if (label) {
            // Text (NVDA): spell out "recommendation of …" because the em-dash
            // reads as a long pause and "3 — Hold" can land as just "3 Hold".
            // HTML (visual): keep the compact "3 — Hold" form.
            text += ` STARS rating was ${starsEntry.rank}, recommendation of ${label}.`;
            html += ` STARS rating was <strong>${starsEntry.rank} — ${label}</strong>.`;
        }
    }

    const targetEntry = findNearestOlderOrEqual(data.target_price, chosenPoint.date);
    if (targetEntry) {
        const targetStr = formatCurrencyPrice(targetEntry.value, currency);
        text += ` Target price was ${targetStr}.`;
        html += ` Target price was <strong>${targetStr}</strong>.`;
    }

    return { text, html };
}
